<script lang="ts" setup>
  import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, FileText } from 'lucide-vue-next'
  import { ref, watch, computed } from 'vue'
  import Button from '@/components/ui/button/Button.vue'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      of: 'of',
      pageOf: 'Page {current} of {total}',
      pageNumberInputLabel: 'Page number, 1 to {total}',
      fitToPage: 'Fit to page',
      previousPage: 'Previous page',
      nextPage: 'Next page',
      zoomOut: 'Zoom out',
      zoomIn: 'Zoom in'
    },
    de: {
      of: 'von',
      pageOf: 'Seite {current} von {total}',
      pageNumberInputLabel: 'Seitennummer, 1 bis {total}',
      fitToPage: 'An Seite anpassen',
      previousPage: 'Vorherige Seite',
      nextPage: 'Nächste Seite',
      zoomOut: 'Verkleinern',
      zoomIn: 'Vergrößern'
    }
  })

  const props = defineProps({
    currentPage: {
      type: Number,
      required: true
    },
    totalPages: {
      type: Number,
      required: true
    },
    showPrevNextButtons: {
      type: Boolean,
      required: false,
      default: true
    },
    showPageButtons: {
      type: Boolean,
      required: false,
      default: true
    },
    // Virtual page support
    currentVirtualPage: {
      type: Number,
      required: false
    },
    totalVirtualPages: {
      type: Number,
      required: false
    },
    isVirtualMode: {
      type: Boolean,
      required: false,
      default: false
    },
    // Zoom controls
    showZoomControls: {
      type: Boolean,
      required: false,
      default: false
    },
    zoomLevel: {
      type: Number,
      required: false,
      default: 1
    },
    fitPage: {
      type: Boolean,
      required: false,
      default: true
    },
    isManualZoom: {
      type: Boolean,
      required: false,
      default: false
    },
    minZoom: {
      type: Number,
      required: false,
      default: 0.5
    },
    maxZoom: {
      type: Number,
      required: false,
      default: 3.0
    },
    displayZoom: {
      type: Number,
      required: false,
      default: 100
    },
    themeColor: {
      type: String,
      required: false,
      default: 'blue'
    }
  })

  const emit = defineEmits(['updatePage', 'updateVirtualPage', 'zoomChange', 'fitToPage'])

  // Computed properties for current values based on mode
  const currentPageValue = computed(() => {
    return props.isVirtualMode ? props.currentVirtualPage || 1 : props.currentPage
  })

  const totalPagesValue = computed(() => {
    return props.isVirtualMode ? props.totalVirtualPages || 1 : props.totalPages
  })

  const inputValue = ref(currentPageValue.value.toString())

  // Watch for external page changes and update input
  watch(currentPageValue, (newPage) => {
    inputValue.value = newPage.toString()
  })

  const goToPreviousPage = () => {
    if (currentPageValue.value > 1) {
      if (props.isVirtualMode) {
        emit('updateVirtualPage', currentPageValue.value - 1)
      } else {
        emit('updatePage', currentPageValue.value - 1)
      }
    }
  }

  const goToNextPage = () => {
    if (currentPageValue.value < totalPagesValue.value) {
      if (props.isVirtualMode) {
        emit('updateVirtualPage', currentPageValue.value + 1)
      } else {
        emit('updatePage', currentPageValue.value + 1)
      }
    }
  }

  const handleInputChange = (event: Event) => {
    const target = event.target as HTMLInputElement
    const value = parseInt(target.value)

    if (!isNaN(value) && value >= 1 && value <= totalPagesValue.value) {
      if (props.isVirtualMode) {
        emit('updateVirtualPage', value)
      } else {
        emit('updatePage', value)
      }
    } else {
      // Reset to current page if invalid
      inputValue.value = currentPageValue.value.toString()
    }
  }

  const handleInputKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleInputChange(event)
      ;(event.target as HTMLInputElement).blur()
    } else if (event.key === 'Escape') {
      inputValue.value = currentPageValue.value.toString()
      ;(event.target as HTMLInputElement).blur()
    }
  }

  const handleInputBlur = (event: Event) => {
    handleInputChange(event)
  }

  const internalZoomLevel = ref(props.zoomLevel)
  const internalFitPage = ref(props.fitPage)
  const internalIsManualZoom = ref(props.isManualZoom)

  const effectiveZoom = computed(() => {
    if (internalFitPage.value) {
      return internalZoomLevel.value
    }
    return internalZoomLevel.value
  })

  const displayEffectiveZoom = computed(() => {
    return Math.round(effectiveZoom.value * 100)
  })

  const handleZoomIn = () => {
    if (internalFitPage.value) {
      internalZoomLevel.value = Math.min(props.maxZoom, effectiveZoom.value + 0.25)
      internalFitPage.value = false
      internalIsManualZoom.value = true
    } else {
      internalZoomLevel.value = Math.min(props.maxZoom, internalZoomLevel.value + 0.25)
      internalIsManualZoom.value = true // Enable fit to page button after manual zoom
    }
    emit('zoomChange', {
      zoomLevel: internalZoomLevel.value,
      fitPage: internalFitPage.value,
      isManualZoom: internalIsManualZoom.value
    })
  }

  const handleZoomOut = () => {
    if (internalFitPage.value) {
      internalZoomLevel.value = Math.max(props.minZoom, effectiveZoom.value - 0.25)
      internalFitPage.value = false
      internalIsManualZoom.value = true
    } else {
      internalZoomLevel.value = Math.max(props.minZoom, internalZoomLevel.value - 0.25)
      internalIsManualZoom.value = true // Enable fit to page button after manual zoom
    }
    emit('zoomChange', {
      zoomLevel: internalZoomLevel.value,
      fitPage: internalFitPage.value,
      isManualZoom: internalIsManualZoom.value
    })
  }

  const handleFitToPage = () => {
    // Emit fitToPage event for parent to handle, since the parent has access to the pdfContainerRef and pageScales
    emit('fitToPage')
  }

  watch(
    () => props.zoomLevel,
    (newZoom) => {
      internalZoomLevel.value = newZoom
    }
  )

  watch(
    () => props.fitPage,
    (newFitPage) => {
      internalFitPage.value = newFitPage
    }
  )

  watch(
    () => props.isManualZoom,
    (newIsManualZoom) => {
      internalIsManualZoom.value = newIsManualZoom
    }
  )
</script>

<template>
  <div class="flex items-center gap-3">
    <!-- Page Navigation: status text "Page X of Y" (non-interactive), Previous/Next (interactive) -->
    <div
      v-show="totalPagesValue > 1"
      class="flex items-center justify-center gap-3"
      role="navigation"
      aria-label="Document pages"
    >
      <!-- Non-interactive status for screen readers: "Page 1 of 3" -->
      <span
        role="status"
        aria-live="polite"
        aria-atomic="true"
        class="sr-only"
      >
        {{ t('pageOf', { current: currentPageValue, total: totalPagesValue }) }}
      </span>
      <!-- Previous button -->
      <button
        v-if="showPrevNextButtons"
        type="button"
        :disabled="currentPageValue === 1"
        class="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-500 transition
          hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 md:h-8 md:min-h-0 md:w-8
          md:min-w-0"
        :aria-label="t('previousPage')"
        @click="goToPreviousPage"
      >
        <ChevronLeft
          class="h-4 w-4"
          aria-hidden="true"
        />
      </button>

      <!-- Page input and total (visible); sr-only live region announces "Page X of Y" on change -->
      <div class="flex items-center gap-2">
        <input
          v-model="inputValue"
          type="text"
          inputmode="numeric"
          :aria-label="t('pageNumberInputLabel', { total: totalPagesValue })"
          class="h-8 w-12 rounded-md border border-purple-300 bg-gray-100 px-2 text-center text-sm font-medium
            text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
          @blur="handleInputBlur"
          @keydown="handleInputKeydown"
        />
        <span class="text-sm text-gray-600">{{ t('of') }}</span>
        <span class="text-sm font-medium text-gray-900">{{ totalPagesValue }}</span>
      </div>

      <!-- Next button -->
      <button
        v-if="showPrevNextButtons"
        type="button"
        :disabled="currentPageValue === totalPagesValue"
        class="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-500 transition
          hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 md:h-8 md:min-h-0 md:w-8
          md:min-w-0"
        :aria-label="t('nextPage')"
        @click="goToNextPage"
      >
        <ChevronRight
          class="h-4 w-4"
          aria-hidden="true"
        />
      </button>
    </div>

    <!-- Zoom Controls -->
    <div
      v-if="showZoomControls"
      class="flex items-center gap-2"
    >
      <Button
        variant="secondary"
        size="sm"
        :disabled="!internalFitPage && internalZoomLevel <= minZoom"
        :aria-label="t('zoomOut')"
        @click="handleZoomOut"
      >
        <ZoomOut class="h-4 w-4" />
      </Button>
      <span class="text-sm font-medium">{{ displayEffectiveZoom }}%</span>
      <Button
        variant="secondary"
        size="sm"
        :disabled="!internalFitPage && internalZoomLevel >= maxZoom"
        :aria-label="t('zoomIn')"
        @click="handleZoomIn"
      >
        <ZoomIn class="h-4 w-4" />
      </Button>
      <Button
        variant="secondary"
        size="sm"
        class="flex items-center gap-1"
        :disabled="!internalIsManualZoom"
        @click="handleFitToPage"
      >
        <FileText class="h-4 w-4" />
        {{ t('fitToPage') }}
      </Button>
    </div>
  </div>
</template>
